import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import services from '@C/Banking/_utils/data';
import {
  formatCurrency,
  TableGrid,
  export2Excel,
  export2PDF,
} from '@C/TableGrid';
import { POSList } from '@C/Banking/_utils/POSList';
import DropLoaderLarge from '@C/Utils/DropLoaderLarge';
import { authHeader } from '@H';

import './styles.scoped.scss';

const POSLedger = () => {
  const [posList, setPosList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bankOptions, setBankOptions] = useState([]);
  const [txid, setTxid] = useState([]);
  const [posData, setPOSData] = useState({
    offset: 1,
    count: 0,
    limit: 20,
    bank_id: '',
    start_date: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
    end_date: dayjs().format('YYYY-MM-DD'),
    time_stamp: dayjs().format('YYYY-MM-DD'),
    uuid: '',
    refreshList: false,
  });

  const limitInput = useRef();
  const posListRef = useRef();

  const col = [
    {
      name: 'Select',
      key: 'all',
      formatter(props) {
        return (
          <>
            {' '}
            <input value={props.uuid} type="checkbox" onChange={handleChange} />
          </>
        );
      },
    },
    {
      name: 'Date',
      key: 'time_stamp',
      sortable: true,
      type: 'date',
    },
    {
      name: 'Narration',
      key: 'narration',
    },
    {
      name: 'Amount credited',
      key: 'all',
      formatter(props) {
        return <>{formatCurrency(props.amount)}</>;
      },
    },
    {
      name: 'Settlement date',
      key: 'settlement_date',
      type: 'date',
    },
  ];

  useEffect(() => {
    getMappedPOS();
  }, []);

  useEffect(() => {
    if (posData.bank_id) {
      getPOSLedger();
    }
  }, [posData.refreshList]);

  const getPOSLedger = () => {
    setLoading(true);
    services
      .getPOSLedger(posData)
      .then((response) => {
        const tmp = response.data.data.results;
        setPOSData((prevData) => ({
          ...prevData,
          count: response.data.data.count,
        }));
        setPosList(tmp);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  const getMappedPOS = () => {
    services
      .getMappedPOS()
      .then((response) => {
        const tmp = response.data.data;
        const CategoryOptions = tmp.map((item) => ({
          value: item.bunk_card_uuid,
          label: item.name,
        }));
        setBankOptions(CategoryOptions);
        if (CategoryOptions.length > 0) {
          const defaultBankId = CategoryOptions[0].value;
          setPOSData((prevData) => ({
            ...prevData,
            bank_id: defaultBankId,
            refreshList: !prevData.refreshList,
          }));
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleQuickSearch = (e) => {
    if (e.target.value === '' || e.target.value === '0') {
      return;
    }
    setPOSData({
      ...posData,
      limit: e.target.value,
      refreshList: !posData.refreshList,
    });
  };

  // const handlePagination = (e) => {
  //   setPOSData(
  //     { ...posData, offset: e, refreshList: !posData.refreshList },
  //     function () {
  //       getPOSLedger();
  //     },
  //   );
  // };

  const handleChange = (e) => {
    if (e.target.checked) {
      setTxid((prevState) => [...prevState, e.target.value]);
    } else {
      const arr = txid.filter((item) => item !== e.target.value);
      setTxid(arr);
    }
  };

  const handleDate = (event) => {
    const value = event.target.value;
    setPOSData(() => ({
      ...posData,
      time_stamp: value,
    }));
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;

    setPOSData((prevWalletData) => {
      if (
        name === 'start_date' &&
        dayjs(value).isAfter(prevWalletData.end_date)
      ) {
        return {
          ...prevWalletData,
          [name]: value,
          end_date: value,
        };
      } else if (
        name === 'end_date' &&
        dayjs(value).isBefore(prevWalletData.start_date)
      ) {
        return {
          ...prevWalletData,
          [name]: value,
          start_date: value,
        };
      } else {
        return {
          ...prevWalletData,
          [name]: value,
        };
      }
    });
    setLoading(false);
  };

  const handlePagination = (e) => {
    setPOSData((prevData) => ({
      ...prevData,
      offset: e,
      refreshList: !prevData.refreshList,
    }));
  };

  const handleDropChange = (e) => {
    setPOSData((prevData) => ({
      ...prevData,
      bank_id: e?.value,
    }));
  };

  const resetFilter = () => {
    posListRef.current.handleReset();
    setPOSData({
      offset: 1,
      count: 0,
      limit: 20,
      bank_id: bankOptions[0]?.value || '',
      start_date: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
      end_date: dayjs().format('YYYY-MM-DD'),
      time_stamp: dayjs().format('YYYY-MM-DD'),
      refreshList: !posData.refreshList,
      exportingPdf: false,
      exportingExcel: false,
    });
    setTxid([]);
  };

  const submitForm = () => {
    if (txid.length === 0) {
      toast.error('Please select transactions to mark as settled.');
      return;
    }
    const headers = authHeader(1);
    axios({
      method: 'get',
      url: `${process.env.API_URL}/payment/settlement/?date=${posData.time_stamp}&tx_id=${txid.join(',')}`,
      headers: headers,
    })
      .then((response) => {
        toast.success('Transactions marked as settled successfully');
        setPOSData({ ...posData, refreshList: !posData.refreshList });
      })
      .catch((error) => {
        toast.error(error.response.data.message);
      });
  };

  const applyFilter = (e) => {
    e.preventDefault();
    setLoading(true);
    setPOSData((prevData) => ({
      ...prevData,
      refreshList: !prevData.refreshList,
    }));
  };

  const exportOptions = {
              fileName: `POS Ledger  - ${posData.start_date} to ${posData.end_date}`,
              reportName: 'POS Ledger',
              reportDate: `Date - ${dayjs(posData.start_date).format(
                'D MMM YY',
              )} to ${dayjs(posData.end_date).format('D MMM YY')}`,
              reportQuantity: '',
              reportSales: '',
              reportTesting: '',
              reportBowser: '',
              reportTotal: '',
              reportCustomerName: '',
              reportGst: '',
              reportOpeningBalance: '',
              reportClosingBalance: '',
              reportSaleTotal: '',
              reportTCSTotal: '',
            };

  return (
    <>
      <section className="widget full-width list-table inverted">
        <header>
          <div className="stock-info-headers">
            <h3> POS Settlement ({posData.count})</h3>
          </div>
          <div className="action">
            <button className="submit" type="submit" onClick={applyFilter}>
              Filter
            </button>
            <button onClick={resetFilter} className="reset-filter"></button>
          </div>
          <div className="export-btns">
            <button
              onClick={() => export2Excel(exportOptions)}
              className={`blue-btn btn-with-icon btn-with-icon__excel action-btn ${posData.exportingExcel ? 'loading' : ''}`}
              title="Export to Excel"
            >
              Export to Excel
            </button>
            <button
              onClick={() => export2PDF(exportOptions)}
              className={`blue-btn btn-with-icon btn-with-icon__pdf action-btn ${posData.exportingPdf ? 'loading' : ''}`}
              title="Export to PDF"
            >
              Export to PDF
            </button>
          </div>
        </header>
        <main>
          <div className="table-filters">
            <div className="filter-wrapper">
              <div className="filter-group">
                <label>From date:</label>
                <input
                  className="cstm-date-input"
                  type="date"
                  value={posData.start_date}
                  name="start_date"
                  onChange={handleDateChange}
                />
              </div>
              <div className="filter-group">
                <label>To date:</label>
                <input
                  className="cstm-date-input"
                  type="date"
                  value={posData.end_date}
                  onChange={handleDateChange}
                  name="end_date"
                />
              </div>
              <div className="filter-group">
                <label>Select POS machine:</label>
                <POSList
                  value={posData.bank_id}
                  handleBankChange={handleDropChange}
                  ref={posListRef}
                />
              </div>
            </div>
          </div>
          <div className="form-header">
            <div className="input-wrapper with-label">
              <label> Select Settlement Date </label>
              <input
                className="cstm-date-input"
                type="date"
                value={posData.time_stamp}
                name="time_stamp"
                onChange={handleDate}
              />
            </div>

            <div className="form-actions">
              <button className="submit" type="submit" onClick={submitForm}>
                Mark as Settled
              </button>
            </div>
          </div>
          <div className="table-wrapper">
            {(() => {
              if (loading) {
                return <DropLoaderLarge />;
              } else {
                return (
                  <>
                    <TableGrid
                      col={col}
                      row={posList}
                      offset={posData.offset}
                      count={posData.count}
                      limit={posData.limit}
                      handlePagination={handlePagination}
                      hideActions={true}
                      enableExport={false}
                      selectedList={txid}
                      recordsFilter={handleQuickSearch}
                    />
                  </>
                );
              }
            })()}
          </div>
        </main>
      </section>
    </>
  );
};

export { POSLedger };
